import { Link } from "react-router-dom";
import { PhoneCall, FileText, Code2, Rocket, CheckCircle, ArrowRight } from "lucide-react";
import { SEOHead, serviceSchema, breadcrumbSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const STEPS = [
  {
    n: "01", icon: PhoneCall, title: "Book a Free Call",
    desc: "Jump on a quick 15-minute call with our Melbourne team. We'll learn about your business, goals, and what you need from your website. No tech talk — just a friendly chat.",
    detail: ["15-min Zoom or phone call", "Understand your business & goals", "Recommend the right package", "Answer all your questions"],
    color: "bg-[#FFD600]"
  },
  {
    n: "02", icon: FileText, title: "Share Your Details",
    desc: "We send you a simple questionnaire. You fill in your business info, upload your logo and photos, and tell us the pages you want. That's it. We handle everything else.",
    detail: ["Simple online form", "Upload your logo & photos", "List your services/products", "We guide you every step"],
    color: "bg-[#0055FF] text-white"
  },
  {
    n: "03", icon: Code2, title: "We Build Your Website",
    desc: "Our team gets to work immediately. Using your info, we design and build a beautiful, fast, mobile website — optimised for Google and tailored to your business.",
    detail: ["Custom design for your brand", "Mobile responsive + fast", "SEO optimised from day one", "Google Analytics + tools setup"],
    color: "bg-black text-white"
  },
  {
    n: "04", icon: Rocket, title: "Launch & Thrive",
    desc: "Your website goes live. We walk you through how to use it. Then watch the enquiries roll in. We're here for ongoing support whenever you need us.",
    detail: ["Live within 48 hours", "Full walkthrough & training", "30-day post-launch support", "Ongoing plans available"],
    color: "bg-[#FFD600]"
  },
];

const WHYS = [
  "No technical knowledge required — we do everything",
  "Guaranteed live within 48 hours of receiving your content",
  "Friendly Melbourne team available by phone & email",
  "No hidden fees, no lock-in contracts",
  "SEO-ready so Google can find you from day one",
  "Mobile responsive on every device, every screen size",
  ".com.au domain $10/yr + hosting $99/yr",
  "100% Aussie made and 100% Aussie supported",
];

export default function HowItWorksPage() {
  return (
    <main className="pt-16" data-testid="how-it-works-page">
      <SEOHead
        title="How It Works — Our 4-Step Process"
        description="Getting a professional website is easy with No Fuss Websites. Book a free call, share your details, we build it in 48 hours, then launch. Simple, fast, and stress-free web design."
        path="/how-it-works"
        schema={[serviceSchema("Web Design", "Professional website design and development for Australian businesses", 499), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "How It Works", path: "/how-it-works" }])]}
      />
      <Breadcrumbs items={[{ name: "How It Works", path: "/how-it-works" }]} />
      {/* Hero */}
      <section className="bg-[#FFD600] border-b-4 border-black section-padding">
        <div className="container-main text-center">
          <p className="text-sm font-black uppercase tracking-widest mb-4">The Process</p>
          <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
            Simple.<br />Fast.<br />No Fuss.
          </h1>
          <p className="text-xl font-semibold max-w-xl mx-auto">
            Getting your website live in 48 hours is easier than you think. Here's exactly how it works.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="section-padding" data-testid="steps-section">
        <div className="container-main">
          <div className="space-y-12">
            {STEPS.map((step, i) => (
              <div
                key={i}
                className={`flex flex-col ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} gap-8 items-center`}
                data-testid={`step-detail-${i + 1}`}
              >
                {/* Icon/Number */}
                <div className={`${step.color} border-2 border-black rounded-3xl p-10 flex-shrink-0 w-full md:w-72 flex flex-col items-center justify-center text-center`}
                  style={{ boxShadow: "6px 6px 0px 0px rgba(0,0,0,1)" }}>
                  <div className="text-7xl font-black opacity-20 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>{step.n}</div>
                  <step.icon size={48} className="mb-4" />
                  <h3 className="text-2xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>{step.title}</h3>
                </div>
                {/* Content */}
                <div className="flex-1">
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">{step.desc}</p>
                  <ul className="space-y-3">
                    {step.detail.map((d, j) => (
                      <li key={j} className="flex items-center gap-3">
                        <CheckCircle size={18} className="text-green-600 shrink-0" />
                        <span className="font-semibold">{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="section-padding bg-black text-white" data-testid="why-us-section">
        <div className="container-main">
          <div className="text-center mb-14">
            <p className="text-sm font-black uppercase tracking-widest text-[#FFD600] mb-3">Why No Fuss</p>
            <h2 className="text-4xl md:text-5xl font-black text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              The No Fuss Difference
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {WHYS.map((w, i) => (
              <div key={i} className="flex items-center gap-4 bg-white/10 border border-white/20 rounded-xl px-6 py-4 hover:bg-white/20 transition-colors" data-testid={`why-item-${i}`}>
                <CheckCircle size={20} className="text-[#FFD600] shrink-0" fill="#FFD600" />
                <span className="font-semibold">{w}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-black border-t-4 border-[#FFD600]">
        <div className="container-main text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Ready to Get Started?
          </h2>
          <p className="text-xl font-semibold text-gray-300 mb-8">Your website could be live in the next 48 hours.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/purchase" className="btn-primary text-lg inline-flex items-center gap-2" data-testid="hiw-cta-contact">
              Get My Website — $499 <ArrowRight size={18} />
            </Link>
            <a href="tel:0466476264" className="bg-white text-black font-bold border-2 border-white px-8 py-3 rounded-full text-lg inline-flex items-center gap-2 hover:bg-[#FFD600] hover:border-[#FFD600] transition-colors" data-testid="hiw-cta-call">
              Call 0466 476 264
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
